"use client";

import { useEffect } from "react";
import { useToast } from "@/contexts/ToastContext";

// Preview/dev origins never carry a service worker by design — the PWA build
// is disabled here (next.config.ts: `disable: NODE_ENV === "development"`).
// But a worker registered during a PAST production-mode run on the same
// origin persists in the browser indefinitely, independent of what today's
// dev server serves — nothing about a normal reload/relogin removes it. That
// stale worker is what served cached JS on vdev after a code fix had already
// landed and restarted the server (2026-07-28 incident). This guard makes
// non-production origins self-heal on every load instead of relying on
// someone remembering to manually unregister it in DevTools.
const NON_PROD_HOST = /^(localhost|127\.0\.0\.1|vdev(-preview)?\.(vbrain\.io|gam\.co\.il))$/;

export function ServiceWorkerVersionGuard() {
  const { toast } = useToast();

  useEffect(() => {
    if (typeof window === "undefined" || !("serviceWorker" in navigator)) return;

    if (NON_PROD_HOST.test(window.location.hostname)) {
      navigator.serviceWorker.getRegistrations().then((regs) => {
        for (const reg of regs) reg.unregister();
      });
      if ("caches" in window) {
        caches.keys().then((keys) => keys.forEach((k) => caches.delete(k)));
      }
      return;
    }

    // Production: skipWaiting + clientsClaim (src/app/sw.ts) mean a new SW
    // version takes control automatically, but the already-loaded tab keeps
    // running its old JS until reloaded. `controllerchange` fires once when a
    // page is first claimed by any worker (not an update) and again on every
    // real version swap after that — skip the first to avoid a false prompt
    // on a page's very first load.
    let sawInitialController = !!navigator.serviceWorker.controller;
    const handleControllerChange = () => {
      if (!sawInitialController) {
        sawInitialController = true;
        return;
      }
      toast({
        type: "info",
        message: "גרסה חדשה זמינה",
        duration: 0,
        action: { label: "רענן עכשיו", onClick: () => window.location.reload() },
      });
    };
    navigator.serviceWorker.addEventListener("controllerchange", handleControllerChange);
    return () => navigator.serviceWorker.removeEventListener("controllerchange", handleControllerChange);
  }, [toast]);

  return null;
}
